import React, { Component } from 'react';

/**
 * Error Boundary component to catch JavaScript errors anywhere in the child component tree.
 * Prevents the entire app from crashing when a single component fails.
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to console for debugging
        console.error('[ErrorBoundary] Caught error:', error);
        console.error('[ErrorBoundary] Error info:', errorInfo);

        this.setState({ errorInfo });

        // You could also log to an error reporting service here
        // e.g., Sentry, LogRocket, etc.
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // Fallback UI when an error occurs
            return (
                <div style={styles.container}>
                    <div style={styles.content}>
                        <div style={styles.iconContainer}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="#EF3340" strokeWidth="2" style={styles.icon}>
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="12"/>
                                <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                        </div>
                        <h1 style={styles.title}>Oeps! Er ging iets mis</h1>
                        <p style={styles.message}>
                            Er is een onverwachte fout opgetreden.
                            Probeer de pagina te herladen of ga terug naar de startpagina.
                        </p>
                        <div style={styles.buttons}>
                            <button onClick={this.handleReload} style={styles.primaryButton}>
                                Pagina herladen
                            </button>
                            <button onClick={this.handleGoHome} style={styles.secondaryButton}>
                                Naar startpagina
                            </button>
                        </div>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Technische details</summary>
                                <pre style={styles.errorText}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#faf4ee',
        padding: '1rem',
        fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif'
    },
    content: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
    },
    iconContainer: {
        marginBottom: '1.5rem'
    },
    icon: {
        width: '64px',
        height: '64px'
    },
    title: {
        color: '#4a3728',
        fontSize: '1.5rem',
        fontWeight: '700',
        margin: '0 0 1rem 0'
    },
    message: {
        color: '#7a6a5a',
        fontSize: '1rem',
        lineHeight: '1.5',
        margin: '0 0 1.5rem 0'
    },
    buttons: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem'
    },
    primaryButton: {
        backgroundColor: '#EF3340',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        padding: '0.875rem 1.5rem',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    secondaryButton: {
        backgroundColor: 'transparent',
        color: '#4a3728',
        border: '2px solid #e0d5c8',
        borderRadius: '12px',
        padding: '0.875rem 1.5rem',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'border-color 0.2s'
    },
    details: {
        marginTop: '1.5rem',
        textAlign: 'left'
    },
    summary: {
        cursor: 'pointer',
        color: '#7a6a5a',
        fontSize: '0.875rem'
    },
    errorText: {
        backgroundColor: '#f5f5f5',
        padding: '1rem',
        borderRadius: '8px',
        fontSize: '0.75rem',
        overflow: 'auto',
        maxHeight: '200px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    }
};

export default ErrorBoundary;
